import { useState, useCallback } from 'react';
import { Droplets, Check, FlaskConical, AlertCircle } from 'lucide-react';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Emulsification Test',
    content: 'Surfactants (soaps and detergents) can emulsify oil in water. They work by having a hydrophilic (water-loving) head and a hydrophobic (oil-loving) tail. The molecules arrange themselves at the oil-water interface, reducing surface tension and allowing oil droplets to be dispersed in water.',
    whatHappens: 'When shaken with oil and water, both soap and detergent form milky emulsions. The surfactant molecules surround oil droplets, preventing them from coalescing.',
    observe: [
      'Oil disperses into tiny droplets',
      'Solution becomes milky/cloudy',
      'Emulsion may separate over time',
      'Detergent often forms more stable emulsions'
    ]
  },
  2: {
    title: 'Hard Water Test',
    content: 'Hard water contains Ca²⁺ and Mg²⁺ ions. Soaps (fatty acid salts) react with these ions to form insoluble precipitates (soap scum):\n\n2 C₁₇H₃₅COO⁻ + Ca²⁺ → (C₁₇H₃₅COO)₂Ca↓\n\nDetergents (sulfate/sulfonate salts) do not form precipitates because their calcium salts are water-soluble.',
    whatHappens: 'Soap forms a white, curd-like precipitate (soap scum) with hard water, while detergent remains clear and effective.',
    observe: [
      'Soap: White precipitate forms',
      'Soap: Solution becomes cloudy',
      'Detergent: No precipitate',
      'Detergent: Lathers normally'
    ]
  },
  3: {
    title: 'pH Test',
    content: 'Soaps are salts of weak acids (fatty acids) and strong bases (NaOH), making them basic in water due to hydrolysis:\n\nC₁₇H₃₅COO⁻ + H₂O ⇌ C₁₇H₃₅COOH + OH⁻\n\nDetergents are salts of strong acids (sulfuric acid) and strong bases, so they are nearly neutral.',
    whatHappens: 'pH testing reveals that soap solutions are basic (pH 9-10) while detergent solutions are near neutral (pH 7-8).',
    observe: [
      'Soap: pH paper shows 9-10',
      'Detergent: pH paper shows 7-8',
      'Soap solution feels slippery',
      'Basic soap can irritate sensitive skin'
    ]
  },
  4: {
    title: 'Acid Reaction Test',
    content: 'When acid is added to soap, the sodium carboxylate is converted back to the free fatty acid, which is insoluble in water:\n\nC₁₇H₃₅COO⁻Na⁺ + HCl → C₁₇H₃₅COOH↓ + NaCl\n\nDetergents are resistant to acid because the sulfate ester is stable under acidic conditions.',
    whatHappens: 'Soap forms an oily precipitate (free fatty acid) when acid is added, while detergent shows no visible reaction.',
    observe: [
      'Soap: Oily layer forms on top',
      'Soap: Precipitate is fatty acid',
      'Detergent: No visible change',
      'Detergent: Remains effective in acid'
    ]
  }
};

interface Part3PropertyTestingProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

interface TestResult {
  soap: string | null;
  detergent: string | null;
}

interface TestResults {
  emulsification: TestResult;
  hardWater: TestResult;
  pH: TestResult;
  acidReaction: TestResult;
}

export default function Part3PropertyTesting({ onStepComplete, currentStep = 1 }: Part3PropertyTestingProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [currentTest, setCurrentTest] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<TestResults>({
    emulsification: { soap: null, detergent: null },
    hardWater: { soap: null, detergent: null },
    pH: { soap: null, detergent: null },
    acidReaction: { soap: null, detergent: null }
  });
  const [testInProgress, setTestInProgress] = useState(false);

  // Perform a test
  const runTest = useCallback((testType: keyof TestResults) => {
    setCurrentTest(testType);
    setTestInProgress(true);

    // Simulate test running
    setTimeout(() => {
      const results: TestResults = { ...testResults };

      switch (testType) {
        case 'emulsification':
          results.emulsification = {
            soap: 'Good emulsion formed, milky appearance',
            detergent: 'Excellent emulsion, very stable'
          };
          break;
        case 'hardWater':
          results.hardWater = {
            soap: 'White precipitate (soap scum) formed',
            detergent: 'No precipitate, clear solution'
          };
          break;
        case 'pH':
          results.pH = {
            soap: 'pH 9-10 (basic)',
            detergent: 'pH 7-8 (near neutral)'
          };
          break;
        case 'acidReaction':
          results.acidReaction = {
            soap: 'Oily precipitate formed (fatty acid)',
            detergent: 'No visible change'
          };
          break;
      }

      setTestResults(results);
      setTestInProgress(false);

      // Mark step complete
      const stepMap: Record<string, string> = {
        emulsification: 'propertyTesting-1',
        hardWater: 'propertyTesting-2',
        pH: 'propertyTesting-3',
        acidReaction: 'propertyTesting-4'
      };
      if (onStepComplete) onStepComplete(stepMap[testType]);
    }, 2000);
  }, [testResults, onStepComplete]);

  const allTestsComplete = Object.values(testResults).every(
    test => test.soap !== null && test.detergent !== null
  );

  // Test tube visualization
  const TestTube = ({ label, color, hasScum, hasPrecipitate }: {
    label: string;
    color: string;
    hasScum?: boolean;
    hasPrecipitate?: boolean;
  }) => (
    <div className="test-tube-container">
      <svg viewBox="0 0 40 100" width={40} height={100}>
        <defs>
          <linearGradient id={`tube-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(200,220,240,0.5)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="100%" stopColor="rgba(200,220,240,0.4)" />
          </linearGradient>
        </defs>
        {/* Tube */}
        <rect x="8" y="10" width="24" height="70" fill={`url(#tube-${label})`} rx="12" />
        <ellipse cx="20" cy="80" rx="12" ry="5" fill="rgba(200,220,240,0.4)" />
        {/* Liquid */}
        <rect x="10" y="35" width="20" height="42" fill={color} opacity="0.7" rx="10" />
        {/* Foam/scum on top */}
        {hasScum && (
          <g>
            <ellipse cx="20" cy="35" rx="10" ry="4" fill="#F5F5F5" />
            <ellipse cx="17" cy="34" rx="3" ry="2" fill="#FFFFFF" opacity="0.8" />
            <ellipse cx="23" cy="36" rx="2" ry="1.5" fill="#FFFFFF" opacity="0.8" />
          </g>
        )}
        {/* Precipitate at bottom */}
        {hasPrecipitate && (
          <ellipse cx="20" cy="72" rx="8" ry="4" fill="#FFE4B5" opacity="0.9" />
        )}
      </svg>
      <span className="tube-label">{label}</span>
    </div>
  );

  return (
    <LabBench
      experimentName="Property Testing"
      completedCount={Object.values(testResults).filter(t => t.soap !== null).length}
      totalSteps={5}
      equipment={['Test tubes', 'Hard water solution', 'Oil', 'HCl solution']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="property-testing-lab">
        {/* Test Selection */}
        <div className="test-selection">
          <h4>Select Test to Perform</h4>
          <div className="test-buttons">
            <div className="test-btn-group">
              <button
                className={`test-btn ${currentTest === 'emulsification' ? 'active' : ''} ${testResults.emulsification.soap ? 'completed' : ''}`}
                onClick={() => !testResults.emulsification.soap && runTest('emulsification')}
                disabled={testInProgress || !!testResults.emulsification.soap}
              >
                <Droplets size={16} />
                Emulsification
                {testResults.emulsification.soap && <Check size={14} className="check-icon" />}
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(1)}>?</button>
            </div>
            <div className="test-btn-group">
              <button
                className={`test-btn ${currentTest === 'hardWater' ? 'active' : ''} ${testResults.hardWater.soap ? 'completed' : ''}`}
                onClick={() => !testResults.hardWater.soap && runTest('hardWater')}
                disabled={testInProgress || !!testResults.hardWater.soap}
              >
                <FlaskConical size={16} />
                Hard Water
                {testResults.hardWater.soap && <Check size={14} className="check-icon" />}
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(2)}>?</button>
            </div>
            <div className="test-btn-group">
              <button
                className={`test-btn ${currentTest === 'pH' ? 'active' : ''} ${testResults.pH.soap ? 'completed' : ''}`}
                onClick={() => !testResults.pH.soap && runTest('pH')}
                disabled={testInProgress || !!testResults.pH.soap}
              >
                <span className="ph-icon">pH</span>
                pH Test
                {testResults.pH.soap && <Check size={14} className="check-icon" />}
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(3)}>?</button>
            </div>
            <div className="test-btn-group">
              <button
                className={`test-btn ${currentTest === 'acidReaction' ? 'active' : ''} ${testResults.acidReaction.soap ? 'completed' : ''}`}
                onClick={() => !testResults.acidReaction.soap && runTest('acidReaction')}
                disabled={testInProgress || !!testResults.acidReaction.soap}
              >
                <AlertCircle size={16} />
                Acid Reaction
                {testResults.acidReaction.soap && <Check size={14} className="check-icon" />}
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(4)}>?</button>
            </div>
          </div>
        </div>

        {/* Test Visualization */}
        {currentTest && (
          <div className="test-visualization">
            <h4 className="test-title">
              {currentTest === 'emulsification' && 'Emulsification Test'}
              {currentTest === 'hardWater' && 'Hard Water Test'}
              {currentTest === 'pH' && 'pH Test'}
              {currentTest === 'acidReaction' && 'Acid Reaction Test'}
            </h4>

            <div className="test-tubes-row">
              {/* Soap test tube */}
              <div className="sample-group">
                <span className="sample-title">Soap</span>
                <TestTube
                  label="Soap"
                  color={
                    currentTest === 'emulsification' ? '#F5F5DC' :
                    currentTest === 'hardWater' ? '#FFFAF0' :
                    currentTest === 'pH' ? '#E6E6FA' :
                    '#FFF8DC'
                  }
                  hasScum={currentTest === 'emulsification' && !!testResults.emulsification.soap}
                  hasPrecipitate={
                    (currentTest === 'hardWater' && !!testResults.hardWater.soap) ||
                    (currentTest === 'acidReaction' && !!testResults.acidReaction.soap)
                  }
                />
                {testResults[currentTest as keyof TestResults]?.soap && (
                  <div className="result-badge soap-result">
                    {testResults[currentTest as keyof TestResults].soap}
                  </div>
                )}
              </div>

              {/* Detergent test tube */}
              <div className="sample-group">
                <span className="sample-title">Detergent</span>
                <TestTube
                  label="Detergent"
                  color={
                    currentTest === 'emulsification' ? '#E0FFFF' :
                    currentTest === 'hardWater' ? '#F0FFFF' :
                    currentTest === 'pH' ? '#F0FFF0' :
                    '#F8F8FF'
                  }
                  hasScum={currentTest === 'emulsification' && !!testResults.emulsification.detergent}
                />
                {testResults[currentTest as keyof TestResults]?.detergent && (
                  <div className="result-badge detergent-result">
                    {testResults[currentTest as keyof TestResults].detergent}
                  </div>
                )}
              </div>
            </div>

            {/* Test in progress indicator */}
            {testInProgress && (
              <div className="progress-indicator">
                <div className="spinner" />
                <span>Running test...</span>
              </div>
            )}
          </div>
        )}

        {/* Results Table */}
        <div className="results-table">
          <h4>Comparison Results</h4>
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Soap</th>
                <th>Detergent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Emulsification</td>
                <td className={testResults.emulsification.soap ? 'filled' : ''}>
                  {testResults.emulsification.soap || '-'}
                </td>
                <td className={testResults.emulsification.detergent ? 'filled' : ''}>
                  {testResults.emulsification.detergent || '-'}
                </td>
              </tr>
              <tr>
                <td>Hard Water Behavior</td>
                <td className={`${testResults.hardWater.soap ? 'filled' : ''} ${testResults.hardWater.soap ? 'negative' : ''}`}>
                  {testResults.hardWater.soap || '-'}
                </td>
                <td className={`${testResults.hardWater.detergent ? 'filled' : ''} ${testResults.hardWater.detergent ? 'positive' : ''}`}>
                  {testResults.hardWater.detergent || '-'}
                </td>
              </tr>
              <tr>
                <td>pH</td>
                <td className={testResults.pH.soap ? 'filled' : ''}>
                  {testResults.pH.soap || '-'}
                </td>
                <td className={testResults.pH.detergent ? 'filled' : ''}>
                  {testResults.pH.detergent || '-'}
                </td>
              </tr>
              <tr>
                <td>Acid Reaction</td>
                <td className={`${testResults.acidReaction.soap ? 'filled' : ''} ${testResults.acidReaction.soap ? 'negative' : ''}`}>
                  {testResults.acidReaction.soap || '-'}
                </td>
                <td className={`${testResults.acidReaction.detergent ? 'filled' : ''} ${testResults.acidReaction.detergent ? 'positive' : ''}`}>
                  {testResults.acidReaction.detergent || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Completion message */}
        {allTestsComplete && (
          <div className="completion-message">
            <Check size={24} />
            <div>
              <h4>All Tests Complete!</h4>
              <p>Detergents perform better in hard water and acidic conditions, while soaps are more affected by water hardness and pH changes.</p>
            </div>
          </div>
        )}

        <style>{`
          .property-testing-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 700px;
          }

          .test-selection {
            width: 100%;
          }

          .test-selection h4 {
            font-size: 13px;
            color: var(--text-secondary);
            margin: 0 0 12px 0;
          }

          .test-buttons {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .test-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 8px;
            color: var(--text-secondary);
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .test-btn:hover:not(:disabled) {
            border-color: var(--primary);
            color: var(--primary);
          }

          .test-btn.active {
            border-color: var(--primary);
            background: rgba(255, 90, 54, 0.1);
            color: var(--primary);
          }

          .test-btn.completed {
            border-color: #00A86B;
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .test-btn:disabled {
            cursor: default;
            opacity: 0.7;
          }

          .check-icon {
            margin-left: 4px;
          }

          .ph-icon {
            font-weight: 700;
            font-size: 11px;
          }

          .test-visualization {
            background: var(--bg-elevated);
            padding: 20px;
            border-radius: 12px;
            width: 100%;
          }

          .test-title {
            text-align: center;
            font-size: 14px;
            margin: 0 0 20px 0;
            color: var(--text-primary);
          }

          .test-tubes-row {
            display: flex;
            justify-content: center;
            gap: 60px;
          }

          .sample-group {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .sample-title {
            font-size: 12px;
            font-weight: 600;
            color: var(--text-secondary);
          }

          .test-tube-container {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .tube-label {
            font-size: 10px;
            color: var(--text-muted);
          }

          .result-badge {
            font-size: 10px;
            padding: 4px 8px;
            border-radius: 4px;
            max-width: 150px;
            text-align: center;
          }

          .soap-result {
            background: rgba(255, 193, 7, 0.1);
            color: #FFC107;
          }

          .detergent-result {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .progress-indicator {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 12px;
            color: var(--text-secondary);
            font-size: 13px;
          }

          .spinner {
            width: 20px;
            height: 20px;
            border: 2px solid var(--border);
            border-top-color: var(--primary);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .results-table {
            width: 100%;
          }

          .results-table h4 {
            font-size: 13px;
            color: var(--text-secondary);
            margin: 0 0 12px 0;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th, td {
            padding: 10px 12px;
            text-align: left;
            border: 1px solid var(--border);
          }

          th {
            background: var(--bg-elevated);
            font-weight: 600;
            color: var(--text-secondary);
          }

          td {
            background: var(--bg-surface);
            color: var(--text-muted);
          }

          td.filled {
            color: var(--text-primary);
          }

          td.positive {
            background: rgba(0, 168, 107, 0.05);
          }

          td.negative {
            background: rgba(255, 193, 7, 0.05);
          }

          .completion-message {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            border-radius: 12px;
            color: #00A86B;
          }

          .completion-message h4 {
            margin: 0;
            font-size: 14px;
          }

          .completion-message p {
            margin: 4px 0 0 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .test-btn-group {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp5"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
